import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { authenticationService } from "src/services";
import { get, post } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Props
========================= */
interface AddDistrictProps {
  handleClose: () => void;
  fetchDistricts: () => void;
  selectedVendorId: string;
}

const AddDistricts: React.FC<AddDistrictProps> = ({
  handleClose,
  fetchDistricts,
  selectedVendorId
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  /* =========================
     State
  ========================= */
  const [states, setStates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    stateId: "",
    name: "",  // Changed from 'district' to 'name'
    shortName: "",  // Changed from 'shortCode' to 'shortName'
    isActive: true
  });

  /* =========================
     Fetch States
  ========================= */
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedVendorId) {
        setStates([]);
        return;
      }

      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/state/vendor/${selectedVendorId}/findAll`
        );
        setStates(res?.data || []);
      } catch (error) {
        console.error("Failed to fetch states", error);
        showSnackbar("Failed to load states", "error");
      }
    };

    fetchStates();
  }, [APIURL, selectedVendorId, showSnackbar]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedVendorId) {
      showSnackbar("Please select a vendor first", "warning");
      return;
    }

    if (!form.stateId || !form.name) {
      showSnackbar("State and District Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: 0,
        stateId: Number(form.stateId),
        name: form.name,  // Changed from 'district' to 'name'
        shortName: form.shortName || null,  // Changed from 'shortCode' to 'shortName'
        isActive: form.isActive,
        vendorId: selectedVendorId,
        createdBy: "system"
      };

      const res = await post(`${APIURL}/api/miscellaneous/district`, payload);
      console.log('adddistrict', res);
      
      if (res?.statusCode === 200 || res?.statusCode === "200") {
        showSnackbar(res?.message || "District added successfully", "success");
        fetchDistricts();
        handleClose();
      } else {
        showSnackbar(res?.message || "Something went wrong", "error");
      }

    } catch (error: any) {
      showSnackbar(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Add District
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Vendor"
            value={selectedVendorId || "No vendor selected"}
            disabled
            InputProps={{
              readOnly: true
            }}
            helperText="Vendor is selected from parent list"
          />
        </Grid>

        {/* State Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="State"
            name="stateId"
            value={form.stateId}
            onChange={handleChange}
          >
            <MenuItem value="" disabled>Select State</MenuItem>
            {states.map(state => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* District Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="District Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortName"
            value={form.shortName}
            onChange={handleChange}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !selectedVendorId}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddDistricts;